import React, { useRef, useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Divider, Image } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import ValuesStore from "../../store/values-store";
import Utilities from "../../dependencies/custom/react-utilities";

const PrintPreview = ({
  applicant,
  parent,
  emergency,
  files,
  bootstrap,
  admissionID,
}) => {
  const valuesStore = ValuesStore();
  const printRef = useRef();
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");

  useEffect(() => {
    bootstrap(admissionID);
    Utilities.setPropFromStore(valuesStore, setDateFormat);
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${applicant?.firstName}_${applicant?.lastName}_Application`,
  });

  const uploads = files?.filesFromDB;
  const categoryRules = files?.categoryRules;

  // Get passport photo from uploads
  const passportPhoto = uploads?.find(
    (f) => f.category?.toLowerCase() === "passportphoto",
  );

  // const passportSrc =
  //     passportPhoto?.file instanceof File
  //         ? URL.createObjectURL(passportPhoto.file)
  //         : passportPhoto?.url || null;
  const passportSrc = passportPhoto?.url;

  return (
    <div style={{ background: "#f0f2f5" }}>
      <Row>
        <Col xs={24} sm={22} md={24} lg={24} xl={24}>
          <center className="bg-white p-2">
            <Button
              disabled={false}
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <hr />
          </center>
          <div
            style={{
              // boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              borderRadius: 6,
              padding: 30,
              background: "#fff",
            }}
            ref={printRef}
          >
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <h2 style={{ marginBottom: 0 }}>ADMISSION APPLICATION PREVIEW</h2>
              <small className="fw-bold" style={{ color: "#666" }}>
                Application Code: {applicant?.application_code || "Pending"}
              </small>
              <Divider />
            </div>

            {/* Printable content */}
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              {/* === Applicant Details === */}
              <h3 style={sectionHeader}>APPLICANT DETAILS</h3>
              <Row gutter={[16, 8]}>
                <Col span={18}>
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <strong>First Name:</strong> {applicant?.firstName}
                    </Col>
                    <Col span={12}>
                      <strong>Last Name:</strong> {applicant?.lastName}
                    </Col>
                    <Col span={12}>
                      <strong>Middle Name:</strong> {applicant?.middleName}
                    </Col>
                    <Col span={12}>
                      <strong>Date of Birth:</strong>{" "}
                      {dayjs(applicant?.dob).format(dateFormat)}
                    </Col>
                    <Col span={12}>
                      <strong>Gender:</strong> {applicant?.gender}
                    </Col>
                    <Col span={12}>
                      <strong>Nationality:</strong> {applicant?.nationality}
                    </Col>
                    <Col span={12}>
                      <strong>Previous School:</strong>{" "}
                      {applicant?.previousSchool}
                    </Col>
                    <Col span={12}>
                      <strong>Class Applied:</strong> {applicant?.className}
                    </Col>
                    <Col span={12}>
                      <strong>Day/Boarding:</strong> {applicant?.dayBoarding}
                    </Col>
                  </Row>
                </Col>

                {/* Passport Photo */}
                <Col span={6} style={{ textAlign: "center" }}>
                  <div
                    style={{
                      border: "1px solid #ccc",
                      padding: 4,
                      width: 110,
                      height: 130,
                      margin: "0 auto",
                      overflow: "hidden",
                    }}
                  >
                    {passportSrc ? (
                      <Image
                        src={passportSrc}
                        alt="Passport Photo"
                        width={100}
                        height={120}
                        style={{ objectFit: "cover" }}
                        preview={false}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          color: "#aaa",
                        }}
                      >
                        No Passport
                      </div>
                    )}
                  </div>
                  <small style={{ color: "#888" }}>Passport Photo</small>
                </Col>
              </Row>

              <Divider />

              {/* === Parent Details === */}
              <h3 style={sectionHeader}>PARENT / GUARDIAN DETAILS</h3>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <strong>Name:</strong> {parent?.parentName}
                </Col>
                <Col span={12}>
                  <strong>National ID:</strong> {parent?.parentNationalIDNum}
                </Col>
                <Col span={12}>
                  <strong>Relationship:</strong> {parent?.relationshipName}
                </Col>
                <Col span={12}>
                  <strong>Phone (Primary):</strong> {parent?.phonePrimary}
                </Col>
                <Col span={12}>
                  <strong>Phone (Alt):</strong> {parent?.phoneAlt}
                </Col>
                <Col span={12}>
                  <strong>Email:</strong> {parent?.email}
                </Col>
                <Col span={12}>
                  <strong>Occupation:</strong> {parent?.occupation}
                </Col>
                <Col span={24}>
                  <strong>Address:</strong> {parent?.address}
                </Col>
              </Row>

              <Divider />

              {/* === Emergency Contact === */}
              <h3 style={sectionHeader}>EMERGENCY CONTACT</h3>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <strong>Name:</strong> {emergency?.emgName}
                </Col>
                <Col span={12}>
                  <strong>Relationship:</strong> {emergency?.emgRelName}
                </Col>
                <Col span={12}>
                  <strong>Phone:</strong> {emergency?.emgPhone}
                </Col>
              </Row>

              <Divider />

              {/* === Other Uploaded Docs === */}
              <h3 style={sectionHeader}>UPLOADED DOCUMENTS</h3>
              <Row gutter={[16, 12]}>
                {uploads
                  // ?.filter((u) => u?.fileType?.toLowerCase() !== "passportphoto")
                  ?.map((u, i) => (
                    <Col span={12} key={i}>
                      <div className="border border-1 rounded rounded-3 p-2">
                        <a href={u?.url} target="_blank" className="text-muted">
                          <strong>{categoryRules[u?.category]?.name}</strong>
                          <br />
                          <span>{u?.name}</span>
                        </a>
                      </div>
                    </Col>
                  ))}
              </Row>

              {/* <Divider /> */}

              {/* Footer */}
              <div className="mt-2 text-center">
                <p className="text-muted">
                  This is a system-generated document.
                </p>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const sectionHeader = {
  background: "#f5f5f5",
  padding: "6px 10px",
  borderLeft: "4px solid #1890ff",
  fontSize: 15,
  fontWeight: 600,
  marginBottom: 5,
};

export default PrintPreview;
